import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LegalPage, useLegalDocumentHead } from '../legal/components';
import { fetchNotices, formatNoticeDate, type NoticeSummary } from './noticeApi';
import { CategoryBadge } from './CategoryBadge';

type State =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; items: NoticeSummary[]; page: number; hasNext: boolean; loadingMore: boolean };

export default function NoticeList() {
  useLegalDocumentHead('공지사항 | Cupid', false);
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetchNotices(0)
      .then((p) => { if (!cancelled) setState({ kind: 'ready', items: p.items, page: 0, hasNext: p.hasNext, loadingMore: false }); })
      .catch(() => { if (!cancelled) setState({ kind: 'error' }); });
    return () => { cancelled = true; };
  }, []);

  const loadMore = () => {
    if (state.kind !== 'ready' || state.loadingMore) return;
    const next = state.page + 1;
    setState({ ...state, loadingMore: true });
    fetchNotices(next)
      .then((p) => setState({ kind: 'ready', items: [...state.items, ...p.items], page: next, hasNext: p.hasNext, loadingMore: false }))
      .catch(() => setState({ ...state, loadingMore: false }));
  };

  return (
    <LegalPage title="공지사항">
      {state.kind === 'loading' && <p className="py-16 text-center text-gray-500">불러오는 중…</p>}
      {state.kind === 'error' && (
        <p className="py-16 text-center text-gray-500">공지사항을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
      )}
      {state.kind === 'ready' && state.items.length === 0 && (
        <p className="py-16 text-center text-gray-500">등록된 공지사항이 없습니다.</p>
      )}
      {state.kind === 'ready' && state.items.length > 0 && (
        <>
          <ul className="divide-y divide-gray-800">
            {state.items.map((n) => (
              <li key={n.slug}>
                <Link
                  to={`/notices/${n.slug}`}
                  className="group flex flex-col gap-2 py-5 transition-colors sm:flex-row sm:items-center sm:gap-4"
                >
                  <CategoryBadge category={n.category} />
                  <span className="flex-1 font-semibold text-gray-100 transition-colors group-hover:text-white">{n.title}</span>
                  <time dateTime={n.publishedAt} className="text-sm text-gray-500">{formatNoticeDate(n.publishedAt)}</time>
                </Link>
              </li>
            ))}
          </ul>
          {state.hasNext && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={state.loadingMore}
                className="rounded-full px-5 py-2 text-sm text-gray-300 ring-1 ring-inset ring-gray-700 transition-colors hover:text-white disabled:opacity-50"
              >
                {state.loadingMore ? '불러오는 중…' : '더 보기'}
              </button>
            </div>
          )}
        </>
      )}
    </LegalPage>
  );
}
